import { useMemo } from 'react';
import { toZonedTime } from 'date-fns-tz';

import { EvChargeScheduleData } from 'clipsal-cortex-types/src/api/api-evcharger';

import { Schedule } from '../types';

export function mapAPISchedules(schedules: EvChargeScheduleData[]): Schedule[] {
  // Group schedules by group_uuid.
  const grouped = schedules.reduce<Record<string, Schedule>>((acc, schedule) => {
    const groupId = schedule.group_uuid;
    if (!acc[groupId]) {
      acc[groupId] = {
        groupId,
        name: schedule.name,
        daysOfWeek: schedule.weekly_freq_interval,
        startTime: null,
        endTime: null,
        isEditing: false,
      };
    }
    if (schedule.event_action === 'switch_on') {
      acc[groupId].startTime = schedule.event_time;
    } else if (schedule.event_action === 'switch_off') {
      acc[groupId].endTime = schedule.event_time;
    }

    return acc;
  }, {});

  return Object.values(grouped);
}

export type ValidationResult = {
  type: 'VALID' | 'INVALID';
  message: string;
};

export function validateSchedule(schedule: Partial<Schedule>, schedules: Schedule[]): ValidationResult {
  if (!schedule.name) {
    return {
      type: 'INVALID',
      message: 'schedule name is required',
    };
  }

  if (!schedule.daysOfWeek?.length) {
    return {
      type: 'INVALID',
      message: 'at least one weekday required for schedule',
    };
  }

  if (!schedule.startTime || !schedule.endTime) {
    return {
      type: 'INVALID',
      message: 'start and end time are required',
    };
  }

  if (schedule.startTime === schedule.endTime) {
    return {
      type: 'INVALID',
      message: 'schedule start or end cant be same',
    };
  }

  const otherSchedules = schedules.filter((s) => !s.isEditing);
  const isExisting = otherSchedules.some(
    (s) => s.name?.toLocaleLowerCase().trim() === schedule.name!.toLocaleLowerCase().trim()
  );
  if (isExisting) {
    return {
      type: 'INVALID',
      message: 'schedule name must be unique',
    };
  }

  // Other schedules with the same weekdays.
  const schedulesForDay = otherSchedules.filter((s) => {
    // Only include if this schedule shares at least one weekday in common with the current one.
    return s.daysOfWeek.find((day) => schedule.daysOfWeek!.includes(day)) !== undefined;
  });

  if (schedule.startTime && schedulesForDay.find((s) => s.startTime === schedule.startTime)) {
    return {
      type: 'INVALID',
      message: 'times cannot overlap',
    };
  }

  if (schedule.endTime && schedulesForDay.find((s) => s.endTime === schedule.endTime)) {
    return {
      type: 'INVALID',
      message: 'times cannot overlap',
    };
  }

  if (schedule.startTime && schedule.endTime) {
    const [startHours, startMins] = schedule.startTime.split(':');
    const startTimeDate = new Date();
    startTimeDate.setHours(Number(startHours), Number(startMins), 0, 0);

    const [endHours, endMins] = schedule.endTime.split(':');
    const endTimeDate = new Date();
    endTimeDate.setHours(Number(endHours), Number(endMins), 0, 0);
  }

  return { type: 'VALID', message: '' };
}

// Helper function to get the start and end times of a schedule.
function getStartAndEndTimesFromSchedule(schedule: Schedule, currentSiteTime: Date) {
  const [startHours, startMins] = (schedule.startTime || '00:00').split(':').map(Number);
  const [endHours, endMins] = (schedule.endTime || '23:59').split(':').map(Number);

  const startTime = new Date(currentSiteTime);
  startTime.setHours(startHours);
  startTime.setMinutes(startMins);
  startTime.setSeconds(0);
  const endTime = new Date(currentSiteTime);
  endTime.setHours(endHours);
  endTime.setMinutes(endMins);
  endTime.setSeconds(schedule.endTime ? 0 : 59); // If no end time, set to 23:59:59 to cover the full day.

  return { startTime, endTime };
}

/**
 * Check if a single schedule is currently running for a specified timezone.
 *
 * @param schedule - The schedule to check.
 * @param timezone - The timezone to check against.
 * @returns An object containing properties:
 *            - startTime: The start time of the schedule.
 *            - endTime: The end time of the schedule.
 *            - isScheduleRunning: Whether the schedule is currently running.
 */
function checkIfScheduleIsRunning(schedule: Schedule, timezone: string) {
  const currentSiteTime = toZonedTime(new Date(), timezone);
  const { startTime, endTime } = getStartAndEndTimesFromSchedule(schedule, currentSiteTime);
  const isScheduleRunning =
    startTime.getTime() <= currentSiteTime.getTime() && endTime.getTime() >= currentSiteTime.getTime();
  return { startTime, endTime, isScheduleRunning };
}

/**
 * Checks if the current time is within a list of schedules
 *
 * @param schedules - The list of schedules to check.
 * @param timezone - The timezone to check against.
 * @returns An object with metadata about the validation result.
 */
export function useCheckIfCurrentlyInSchedule(
  schedules: Schedule[],
  timezone: string
): {
  isInSchedule: boolean;
  startTime?: Date;
  endTime?: Date;
} {
  const currentSiteTime = toZonedTime(new Date(), timezone);
  const currentWeekdayIndex = currentSiteTime.getDay();
  const schedulesForDay = schedules.filter((s) => s.daysOfWeek.includes(currentWeekdayIndex));

  // Check if we're currently in a schedule, and if not show the most recently completed schedule (if less than
  // 6 hours ago), otherwise show the time until the soonest next scheduled event starts.
  return useMemo(() => {
    for (const schedule of schedulesForDay) {
      const { startTime, endTime, isScheduleRunning } = checkIfScheduleIsRunning(schedule, timezone);
      if (isScheduleRunning) {
        return {
          isInSchedule: true,
          startTime,
          endTime,
        };
      }
    }

    return { isInSchedule: false };
  }, [timezone, schedulesForDay]);
}
